<template>
  <div ref="chatContainer" :class="['chat-container', { 'moving': isMoving, 'minimized': isMinimized, 'initializing': isInitializing, 'wide': isWideContainer }]" :style="containerStyle">
    <ChatHeader
      class="moving-handle"
      :is-connected="isConnected"
      :is-resized="isResized"
      :is-moved="isMoved"
      :is-minimized="isMinimized"
      @minimize="handleMinimize"
      @restore="handleRestore"
      @initialize="handleInitialize"
    />
    <template v-if="!isMinimized">
      <div class="chat-body">
        <div class="chat-body-content">
          <div class="message-list-wrapper">
            <MessageList
              :messages="messages"
              :is-loading="isLoading"
              :is-answering="isAnswering"
              :user-data="userData"
              @at-bottom="isScrollAtBottom = $event"
            />
            <div class="button-wrapper">
              <button v-if="messages.length" class="reset-button" @click="resetHistoryMessages">
                <span class="button-text">{{
                  $t('common.reset')
                }}</span>
                <i class="icon icon--reset-red"></i>
              </button>
            </div>
          </div>
        </div>
        <AskContainer
          :is-loading="isLoading"
          :is-answering="isAnswering"
          :disabled="isAskDisabled"
          :limit-reached="tokenLimitReached"
          :show-fade="isAnswering || !isScrollAtBottom"
          @submit="handleSubmitQuestion"
          @stop="stopAnswering"
          @show-count="showCountWrapper"
          @hide-count="hideCountWrapper"
        />
      </div>
      <div class="count-wrapper" v-show="isCountWrapperShown">
        <template v-if="!questionCount.exceeded">
          <span class="count-title">{{ $t('mine.chat.mcpQuestionLimit') }}</span>
          <span class="count-value">
            {{ questionCount.total - questionCount.count }} / {{ questionCount.total }}
          </span>
        </template>
        <span v-else class="question-alert">
          {{ $t('mine.chat.mcpQuestionLimitAlert') }}
        </span>
      </div>
      <!-- Resize handles -->
      <div class="resize-handle resize-handle-right" @mousedown="startResize('right', $event)"></div>
      <div class="resize-handle resize-handle-bottom" @mousedown="startResize('bottom', $event)"></div>
      <div class="resize-handle resize-handle-left" @mousedown="startResize('left', $event)"></div>
      <div class="resize-handle resize-handle-top" @mousedown="startResize('top', $event)"></div>
      <div class="resize-handle resize-handle-corner resize-handle-bottom-right" @mousedown="startResize('bottom-right', $event)"></div>
      <div class="resize-handle resize-handle-corner resize-handle-bottom-left" @mousedown="startResize('bottom-left', $event)"></div>
      <div class="resize-handle resize-handle-corner resize-handle-top-right" @mousedown="startResize('top-right', $event)"></div>
      <div class="resize-handle resize-handle-corner resize-handle-top-left" @mousedown="startResize('top-left', $event)"></div>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import {
  connect, askQuestion, abortRendering, getTokenStatus, getHistory, heatmapDocentCheck
} from '@/api/ai'
import ChatHeader from './ChatHeader.vue'
import MessageList from './MessageList.vue'
import AskContainer from './AskContainer.vue'
import { makeDraggable } from '@/libs/draggable'

const serviceType = 'ba'
const category = 'journeymapMCP'
const JOURNEY_SID_STORAGE_KEY = `${category}_journeySid`
const MIN_WIDTH = 360
const MIN_HEIGHT = 360
const DEFAULT_HEIGHT = 714
const INITIAL_POSITION = {
  top: 130,
  left: null,
  right: 24
}

export default {
  name: 'ChatContainer',
  components: {
    ChatHeader,
    MessageList,
    AskContainer
  },
  props: {
  },
  data () {
    return {
      isCountWrapperShown: false,
      messages: [],
      isConnected: false,
      isLoading: false,
      isAnswering: false,
      tokenLimitReached: false,
      containerWidth: MIN_WIDTH,
      containerHeight: DEFAULT_HEIGHT,
      containerPosition: { ...INITIAL_POSITION },
      isResizing: false,
      isResized: false,
      isMoving: false,
      isMoved: false,
      removeMovingDraggable: null,
      isMinimized: false,
      isInitializing: false,
      resizeDirection: '',
      prevBrowserWidth: document.documentElement.clientWidth,
      mcpBuffer: '',
      isMcpResponse: null,
      isScrollAtBottom: true,
      questionCount: {
        total: 0,
        count: 0,
        exceeded: false
      }
    }
  },
  computed: {
    ...mapState([
      'userData',
      'journeyOptions',
      'journeyPeriod',
      'gnbMinimized'
    ]),
    minTop () {
      return this.gnbMinimized < 0 ? 10 : 106
    },
    isWideContainer () {
      return this.containerWidth > 440
    },
    isAskDisabled () {
      return this.isLoading || this.isAnswering || this.tokenLimitReached
    },
    chatUserInfo () {
      return {
        lang: this.$i18n.locale,
        email: this.userData.userId,
        organization: this.userData.userName,
        userNum: this.userData.userNum
      }
    },
    containerStyle () {
      return {
        width: this.containerWidth + 'px',
        height: this.containerHeight + 'px',
        top: this.containerPosition.top + 'px',
        right: this.containerPosition.right + 'px',
        transform: this.containerPosition.transform
      }
    }
  },
  async created () {
    const sid = this.$route.query.sid
    const savedSid = sessionStorage.getItem(JOURNEY_SID_STORAGE_KEY)

    if (sid && sid !== savedSid) {
      await this.resetHistoryMessages()
    } else {
      await this.initChat()
    }

    if (sid) {
      sessionStorage.setItem(JOURNEY_SID_STORAGE_KEY, sid)
    }
  },
  mounted () {
    this.onBrowserResize()
    this.initMovingDraggable()
    window.addEventListener('resize', this.onBrowserResize)
  },
  beforeDestroy () {
    if (this.removeMovingDraggable) this.removeMovingDraggable()
    window.removeEventListener('resize', this.onBrowserResize)
  },
  methods: {
    showCountWrapper () {
      this.isCountWrapperShown = true
    },
    hideCountWrapper () {
      this.isCountWrapperShown = false
    },
    async checkQuestionLimit () {
      const params = {
        'category': 'journeymapMCP',
        'serviceType': 'ba'
      }
      try {
        const res = await heatmapDocentCheck(params)
        this.questionCount.total = res.total
        this.questionCount.count = res.count
        this.questionCount.exceeded = !res.state
      } catch (e) {
        console.log(e)
      }
      try {
        const res = await heatmapDocentCheck(params)
        this.questionCount.total = res.total
        this.questionCount.count = res.count
        this.questionCount.exceeded = !res.state
      } catch (e) {
        console.log(e)
      }
    },
    setConnection () {
      this.isConnected = true
    },
    async handleSubmitQuestion (question) {
      if (this.isAskDisabled) return

      await this.checkQuestionLimit()
      if (this.questionCount.exceeded) return

      this.insertMessage({ role: 'human', message: question.prompt })
      this.insertMessage({
        role: 'ai',
        message: ''
      })
      this.isLoading = true

      const segmentKey = ['sid', 'subSid', 'firstPath', 'domain', 'subDomain', 'matchType', 'device', 'exist', 'refer', 'utm', 'survey', 'isCustomNode']
      const segments = segmentKey.reduce((acc, key) => {
        if (this.journeyOptions[key] !== undefined) acc[key] = this.journeyOptions[key]
        return acc
      }, {})

      const questionData = {
        isMcp: true,
        prompt: question.prompt,
        category: category,
        serviceType: serviceType,
        info: this.chatUserInfo,
        segmentData: {
          start: this.journeyPeriod.start,
          end: this.journeyPeriod.end,
          ...segments
        }
      }

      await askQuestion(category, questionData, this.startMcpAnswering, this.onMcpAnswering, this.endMcpAnswering)
    },
    insertMessage (message) {
      this.messages.unshift(message)
    },
    setError (type = 'default') {
      if (this.messages.length && this.messages[0].error !== 'access') {
        this.$set(this.messages[0], 'error', type)
      }
    },
    stopAnswering () {
      abortRendering()
      this.isAnswering = false
    },
    // MCP 전용 콜백: JSON 스트림 파싱
    startMcpAnswering (res) {
      if (this.messages.length) {
        this.messages[0].id = res.headers.get('id')
        this.messages[0].message = ''
      }
      const askCode = res.headers.get('Askcode')
      if (askCode === '599') {
        this.setError()
      } else if (askCode === '403') {
        this.setError('access')
      } else {
        this.isAnswering = true
      }
      this.mcpBuffer = ''
      this.isMcpResponse = null
      this.isLoading = false
    },
    onMcpAnswering (chunk) {
      if (this.isMcpResponse === null) {
        const trimmed = chunk.trim()
        this.isMcpResponse = trimmed.startsWith('{')
      }

      if (!this.isMcpResponse) {
        this.messages[0].message += chunk
        return
      }

      this.mcpBuffer += chunk

      // 여러 JSON 객체가 연속으로 올 수 있으므로 파싱 시도
      const jsonObjects = this.mcpBuffer.split('}{')

      if (jsonObjects.length > 1) {
        // 여러 객체가 있으면 분리해서 처리
        for (let i = 0; i < jsonObjects.length; i++) {
          let jsonStr = jsonObjects[i]
          if (i > 0) jsonStr = '{' + jsonStr
          if (i < jsonObjects.length - 1) jsonStr = jsonStr + '}'

          try {
            const data = JSON.parse(jsonStr)
            this.processMcpData(data)
            if (i === jsonObjects.length - 1) {
              this.mcpBuffer = ''
            }
          } catch {
            // 마지막 불완전한 JSON은 버퍼에 유지
            if (i === jsonObjects.length - 1) {
              this.mcpBuffer = jsonStr
            }
          }
        }
      } else {
        // 단일 객체 시도
        const data = JSON.parse(this.mcpBuffer)
        this.processMcpData(data)
        this.mcpBuffer = ''
      }
    },
    processMcpData (data) {
      const msg = this.messages[0]
      const isError = !!data.is_error
      const errorCode = data.error_code || null

      if (!msg.mcpData) {
        this.$set(msg, 'mcpData', {
          iterations: [],
          model: null,
          currentIteration: -1
        })
      }

      const mcpData = msg.mcpData
      const iterationIndex = data.iteration || 0

      if (iterationIndex > mcpData.currentIteration) {
        mcpData.currentIteration = iterationIndex
        mcpData.iterations.push({
          items: [],
          lastType: null
        })
      }

      const currentIter = mcpData.iterations[mcpData.iterations.length - 1]
      if (!currentIter) return

      if (data.model) {
        mcpData.model = data.model
      }

      const lastItem = currentIter.items[currentIter.items.length - 1]

      if (data.thinking) {
        if (lastItem && lastItem.type === 'thinking') {
          lastItem.content += data.thinking
          lastItem.is_error = isError
          lastItem.error_code = errorCode
        } else {
          currentIter.items.push({ type: 'thinking', content: data.thinking, is_error: isError, error_code: errorCode })
        }
        currentIter.lastType = 'thinking'
      }

      if (data.tool_name) {
        if (lastItem && lastItem.type === 'tool' && lastItem.name === data.tool_name) {
          if (data.tool_text) lastItem.text += data.tool_text
          lastItem.is_error = isError
          lastItem.error_code = errorCode
        } else {
          currentIter.items.push({
            type: 'tool',
            name: data.tool_name,
            text: data.tool_text || '',
            is_error: isError,
            error_code: errorCode
          })
        }
        currentIter.lastType = 'tool'
      } else if (data.tool_text) {
        if (lastItem && lastItem.type === 'tool') {
          lastItem.text += data.tool_text
          lastItem.is_error = isError
          lastItem.error_code = errorCode
        } else {
          currentIter.items.push({
            type: 'tool',
            name: null,
            text: data.tool_text,
            is_error: isError,
            error_code: errorCode
          })
        }
        currentIter.lastType = 'tool'
      }

      if (data.text) {
        if (lastItem && lastItem.type === 'text') {
          lastItem.content += data.text
          lastItem.is_error = isError
          lastItem.error_code = errorCode
        } else {
          currentIter.items.push({ type: 'text', content: data.text, is_error: isError, error_code: errorCode })
        }
        currentIter.lastType = 'text'
      }

      const hasPrimaryContent = data.thinking || data.tool_name || data.tool_text || data.text

      if (!hasPrimaryContent && (isError || errorCode)) {
        currentIter.items.push({
          type: 'is_error',
          content: '',
          is_error: isError,
          error_code: errorCode
        })
        currentIter.lastType = 'error'
      }

      if (errorCode === 'OVER_TOKEN') {
        this.tokenLimitReached = true
      }

      if (isError) {
        this.setError()
      }
    },
    endMcpAnswering (res, error) {
      if (res) {
        const id = res.headers.get('id')
        setTimeout(() => {
          this.checkTokenStatus(id)
        }, 2000)
      }
      if (error) {
        this.setError(error.name === 'AbortError' ? 'aborted' : 'default')
      }

      this.mcpBuffer = ''
      this.isMcpResponse = null
      this.isLoading = false
      this.isAnswering = false
      this.checkQuestionLimit()
    },
    async resetHistoryMessages () {
      this.stopAnswering()
      this.isConnected = false
      this.isAnswering = false
      this.isLoading = false
      this.mcpBuffer = ''
      this.isMcpResponse = null
      this.messages = []
      this.tokenLimitReached = false
      sessionStorage.removeItem(`${category}_accessToken`)
      sessionStorage.removeItem(`${category}_refreshToken`)
      sessionStorage.removeItem(`${category}_sessionKey`)
      try {
        await this.initChat()
      } catch (e) {
        console.log(e)
      }
    },
    checkTokenStatus (id) {
      getTokenStatus(id, category)
        .then((res) => {
          if (res.data.data.alert) {
            this.tokenLimitReached = true
            this.insertMessage({
              role: 'ai',
              error: 'limit'
            })
          }
        })
        .catch((e) => {
          console.log(e)
        })
    },
    async initChat () {
      await this.checkQuestionLimit()
      await connect(category, this.setConnection)
      getHistory(category)
        .then((res) => {
          const messages = res.data.data
          if (messages.length > 0) {
            const reversedMessages = messages.reverse()
            this.messages = [...this.messages, ...reversedMessages]
          }
        })
        .catch((e) => {
          console.log(e)
        })
    },
    enableDragMode (containerEl) {
      document.body.style.userSelect = 'none'
      document.body.style.pointerEvents = 'none'
      if (containerEl) {
        containerEl.style.pointerEvents = 'auto'
      }
    },
    disableDragMode (containerEl) {
      document.body.style.userSelect = ''
      document.body.style.pointerEvents = ''
      if (containerEl) {
        containerEl.style.pointerEvents = ''
      }
    },
    initMovingDraggable () {
      const containerEl = this.$refs.chatContainer
      const handleEl = containerEl && containerEl.querySelector('.moving-handle')
      if (!containerEl || !handleEl) return

      this.removeMovingDraggable = makeDraggable(handleEl, {
        boundsEl: containerEl,
        getPosition: () => this.containerPosition,
        setPosition: position => {
          this.containerPosition = position
        },
        minTop: () => this.minTop,
        shouldStart: event => {
          if (this.isResizing) return false
          if (!this.isMinimized) {
            return !(event.target.closest('.solomon-profile') || event.target.closest('button'))
          }
          return true
        },
        onMoveStart: () => {
          this.isMoving = true
          this.isMoved = true
        },
        onMoveEnd: () => {
          this.isMoving = false
        }
      })
    },
    startResize (direction, e) {
      e.preventDefault()
      e.stopPropagation()
      this.isResizing = true
      this.isResized = true
      this.isMoved = true
      this.resizeDirection = direction

      const containerEl = e.currentTarget.parentElement
      this.enableDragMode(containerEl)

      const startX = e.clientX
      const startY = e.clientY
      const startWidth = this.containerWidth
      const startHeight = this.containerHeight
      const startTop = this.containerPosition.top
      const startRight = this.containerPosition.right

      const handleMouseMove = (e) => {
        if (!this.isResizing) return

        const deltaX = e.clientX - startX
        const deltaY = e.clientY - startY

        let newWidth = startWidth
        let newHeight = startHeight
        let newTop = startTop
        let newRight = startRight

        if (this.resizeDirection.includes('right')) {
          newWidth = Math.max(MIN_WIDTH, startWidth + deltaX)
          const actualWidthChange = newWidth - startWidth
          newRight = startRight - actualWidthChange
          if (newRight < 0) {
            newRight = 0
            newWidth = startWidth + startRight
          }
        }
        if (this.resizeDirection.includes('left')) {
          const desiredWidth = startWidth - deltaX
          newWidth = Math.max(MIN_WIDTH, desiredWidth)
          if (newWidth + newRight > document.documentElement.clientWidth) {
            newWidth = document.documentElement.clientWidth - newRight
          }
        }
        if (this.resizeDirection.includes('bottom')) {
          newHeight = Math.max(MIN_HEIGHT, startHeight + deltaY)
          if (newHeight + newTop > document.documentElement.clientHeight) {
            newHeight = document.documentElement.clientHeight - newTop
          }
        }
        if (this.resizeDirection.includes('top')) {
          const desiredHeight = startHeight - deltaY
          newHeight = Math.max(MIN_HEIGHT, desiredHeight)
          const actualHeightChange = newHeight - startHeight
          newTop = startTop - actualHeightChange
          if (newTop < this.minTop) {
            newTop = this.minTop
            newHeight = startHeight + startTop - this.minTop
          }
        }

        this.containerWidth = newWidth
        this.containerHeight = newHeight
        this.containerPosition = {
          top: newTop,
          right: newRight
        }
      }

      const handleMouseUp = () => {
        this.isResizing = false
        this.resizeDirection = ''
        this.disableDragMode(containerEl)

        document.removeEventListener('mousemove', handleMouseMove, { capture: true })
        document.removeEventListener('mouseup', handleMouseUp, { capture: true })
      }

      document.addEventListener('mousemove', handleMouseMove, { capture: true, passive: false })
      document.addEventListener('mouseup', handleMouseUp, { capture: true, passive: false })
    },
    handleMinimize () {
      this.isMinimized = true
    },
    handleRestore () {
      const browserWidth = document.documentElement.clientWidth
      const browserHeight = document.documentElement.clientHeight

      const maxRight = browserWidth - this.containerWidth
      const maxTop = browserHeight - this.containerHeight

      this.containerPosition = {
        top: Math.max(0, Math.min(this.containerPosition.top, maxTop)),
        right: Math.max(0, Math.min(this.containerPosition.right, maxRight))
      }
      this.isMinimized = false
    },
    onBrowserResize () {
      const browserWidth = document.documentElement.clientWidth
      if (browserWidth < this.prevBrowserWidth) {
        this.containerPosition.right = Math.min(this.containerPosition.right, browserWidth - this.containerWidth)
      } else if (this.containerPosition.right < 0) {
        this.containerPosition.right = 0
      }
      this.prevBrowserWidth = browserWidth
    },
    handleInitialize () {
      this.isInitializing = true

      requestAnimationFrame(() => {
        this.isResized = false
        this.containerWidth = MIN_WIDTH
        this.containerHeight = DEFAULT_HEIGHT
        this.containerPosition = { ...INITIAL_POSITION }
        this.isMoved = false
        this.onBrowserResize()

        setTimeout(() => {
          this.isInitializing = false
        }, 300)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.chat-container {
  position: fixed;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 360px;
  min-height: 360px;
  padding: 8px 16px 16px;
  border-radius: 16px;
  border: 1px solid rgba(26, 26, 26, 0.24);
  background-color: #fff;
  box-shadow: 0px 16px 20px 0px rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
  color: #3d3d3d;
  line-height: 1;
  z-index: 4;
  will-change: transform, top, left, right, width, height; // 하드웨어 가속을 위해 변경 사항 미리 알림
  z-index: 10;

  &.minimized {
    width: auto !important;
    height: auto !important;
    min-width: auto;
    min-height: auto;
    padding: 0;
  }

  &.moving {
    user-select: none;
    cursor: move;
  }

  &.initializing {
    transition: width 0.3s ease-out, height 0.3s ease-out, top 0.3s ease-out, right 0.3s ease-out;
  }

  .moving-handle {
    cursor: move;
    user-select: none;
    touch-action: none;
  }

  .resize-handle {
    position: absolute;
    z-index: 10;
    touch-action: none;

    &.resize-handle-right {
      top: 0;
      right: 0;
      width: 8px;
      height: 100%;
      cursor: ew-resize;
    }

    &.resize-handle-left {
      top: 0;
      left: 0;
      width: 8px;
      height: 100%;
      cursor: ew-resize;
    }

    &.resize-handle-bottom {
      bottom: 0;
      left: 0;
      width: 100%;
      height: 8px;
      cursor: ns-resize;
    }

    &.resize-handle-top {
      top: 0;
      left: 0;
      width: 100%;
      height: 8px;
      cursor: ns-resize;
    }

    &.resize-handle-corner {
      width: 16px;
      height: 16px;

      &.resize-handle-bottom-right {
        width: 24px;
        height: 24px;
        bottom: 0;
        right: 0;
        cursor: nwse-resize;
        background-image: url(../../assets/img/docent/icon--sizeHandler-bottomRight.svg);
        border-top-left-radius: 30px;
      }

      &.resize-handle-bottom-left {
        width: 24px;
        height: 24px;
        bottom: 0;
        left: 0;
        cursor: nesw-resize;
        background-image: url(../../assets/img/docent/icon--sizeHandler-bottomLeft.svg);
        border-top-right-radius: 30px;
      }

      &.resize-handle-top-right {
        top: 0;
        right: 0;
        cursor: nesw-resize;
      }

      &.resize-handle-top-left {
        top: 0;
        left: 0;
        cursor: nwse-resize;
      }
    }
  }

  .count-wrapper {
    position: absolute;
    bottom: -13px;
    right: 31px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px 20px;
    border-radius: 6px;
    background-color: #fff;
    white-space: nowrap;
    box-shadow:
      0 8px 15px 0 rgba(0, 0, 0, 0.16),
      0 0 0 1px rgba(0, 0, 0, 0.16);
    &::before {
      content: '';
      position: absolute;
      width: 10px;
      height: 10px;
      top: 0;
      transform: translate(-50%, -50%) rotate(135deg);
      right: 17px;
      background-color: #fff;
    }
    .count-title {
      color: #444;
      font-size: 12px;
      font-weight: 500;
    }
    .count-value {
      color: #444;
      font-size: 14px;
      font-weight: 600;
    }
    .question-alert {
      font-size: 12px;
      font-weight: 500;
      color: #E92640;
    }
  }

  .count-wrapper {
    position: absolute;
    bottom: -13px;
    right: 31px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px 20px;
    border-radius: 6px;
    background-color: #fff;
    white-space: nowrap;
    box-shadow:
      0 8px 15px 0 rgba(0, 0, 0, 0.16),
      0 0 0 1px rgba(0, 0, 0, 0.16);
    &::before {
      content: '';
      position: absolute;
      width: 10px;
      height: 10px;
      top: 0;
      transform: translate(-50%, -50%) rotate(135deg);
      right: 17px;
      background-color: #fff;
    }
    .count-title {
      color: #444;
      font-size: 12px;
      font-weight: 500;
    }
    .count-value {
      color: #444;
      font-size: 14px;
      font-weight: 600;
    }
    .question-alert {
      font-size: 12px;
      font-weight: 500;
      color: #E92640;
    }
  }

  .chat-body {
    position: relative;
    display: flex;
    flex-direction: column;
    height: calc(100% - 59px);
    width: 100%;
    border: 1px solid #c3c3c3;
    border-radius: 6px;
    background-color: #ececec;
    box-shadow: 0px 3px 6px 0px #0000001f;
    box-sizing: border-box;
    overflow: hidden;

    &-content {
      display: flex;
      flex: 1;
      min-height: 0;
      overflow: hidden;

      .message-list-wrapper {
        position: relative;
        flex: 1;
        min-width: 0;
        overflow: hidden;
        transition: all 0.3s ease-in-out;
        .button-wrapper {
          position: absolute;
          top: 8px;
          right: 8px;
          display: flex;
          gap: 10px;
          button {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px;
            border-radius: 5px;
            font-size: 14px;
            font-weight: 600;
            line-height: 1;
          }
          .reset-button {
            background-color: #fff;
            box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.25);
            color: $brandColor;
            .button-text {
              display: none;
            }
          }
        }
      }
    }
  }

  &.wide {
    .chat-body-content {
      .message-list-wrapper {
        .button-wrapper {
          top: 19px;
          right: 19px;
          .reset-button {
            padding: 11px 12px;
            .button-text {
              display: block;
            }
          }
        }
      }
    }
  }
}
</style>
